/**
 * auto.ts — pure helpers for Aethereum auto mode (hive hook + aethereum init).
 *
 * Config resolution: env vars win over ~/.aethereum/config.json.
 * All network calls use a 1500ms AbortSignal.timeout so they never
 * meaningfully delay the hook budget. Every function swallows errors —
 * auto mode must NEVER break the agent.
 */

import { homedir } from "node:os";
import { readFileSync } from "node:fs";
import { join, resolve } from "node:path";

// ── Config types ────────────────────────────────────────────────────────────

export interface AutoConfig {
  url: string;
  token: string;
}

interface ConfigFile {
  projects?: Record<string, { url?: string; token?: string }>;
  default?: { url?: string; token?: string };
}

/**
 * Context response from GET /api/sync/context.
 * `text` is the formatted team context; `alerts` is the raw contract_changed
 * alert objects so we can format them for injection.
 */
export interface ContextResponse {
  text: string;
  alerts: Array<{
    type: "contract_changed";
    name: string;
    from: number;
    to: number;
    oldShape: string;
    newShape: string;
    ts: number;
  }>;
  contracts: number;
  presence: number;
  /** The curated team changelog (newest LAST), surfaced by `aethereum changelog`. */
  changelog?: Array<{ ts: number; actor: string; kind: string; summary: string }>;
}

// ── loadAutoConfig ──────────────────────────────────────────────────────────

/**
 * Resolve {url, token} from:
 *   1. AETHEREUM_AUTO=off -> always null
 *   2. env AETHEREUM_SYNC_API_URL + AETHEREUM_SYNC_TOKEN
 *   3. ~/.aethereum/config.json: closest ancestor project match, then default
 *
 * @param homeDir  override home directory (for testing; defaults to os.homedir())
 */
export function loadAutoConfig(
  cwd: string,
  env: Record<string, string | undefined> = process.env as Record<string, string | undefined>,
  homeDir?: string,
): AutoConfig | null {
  if ((env["AETHEREUM_AUTO"] ?? "").toLowerCase() === "off") return null;

  const envUrl = env["AETHEREUM_SYNC_API_URL"];
  const envToken = env["AETHEREUM_SYNC_TOKEN"];
  if (envUrl && envToken) {
    return { url: envUrl.replace(/\/$/, ""), token: envToken };
  }

  try {
    const cfgPath = join(homeDir ?? homedir(), ".aethereum", "config.json");
    const raw = readFileSync(cfgPath, "utf8");
    const cfg = JSON.parse(raw) as ConfigFile;

    // Find closest ancestor path match
    const projects = cfg.projects ?? {};
    const absKeys = Object.keys(projects).map((k) => resolve(k));
    const absCwd = resolve(cwd);

    // Sort longest path first so child overrides parent
    const matches = absKeys
      .filter((k) => absCwd === k || absCwd.startsWith(k + "/"))
      .sort((a, b) => b.length - a.length);

    for (const key of matches) {
      // Find the original key (before resolve) to retrieve config
      const origKey = Object.keys(projects).find((k) => resolve(k) === key);
      if (!origKey) continue;
      const p = projects[origKey];
      if (p?.url && p?.token) {
        return { url: p.url.replace(/\/$/, ""), token: p.token };
      }
    }

    // Fall back to default
    const def = cfg.default;
    if (def?.url && def?.token) {
      return { url: def.url.replace(/\/$/, ""), token: def.token };
    }
  } catch {
    // no config file or parse error — silent
  }

  return null;
}

// ── deriveIntent ────────────────────────────────────────────────────────────

const TRIVIAL_RE = /^(y|yes|no|ok|go|continue|sure|thanks?|do it)\b/i;

/**
 * Secret-shaped tokens we must never publish as intent. Auto mode shares a
 * one-line summary of what you're working on; if a prompt line carries a key,
 * we'd rather share nothing than leak it. Errs toward suppression.
 */
// Prefixed provider keys are specific enough to match directly; the generic
// "long high-entropy blob" branch additionally requires a digit so ordinary
// long words (or repeated characters) are not mistaken for credentials.
const SECRET_RE =
  /\b(sk-[A-Za-z0-9]{12,}|gh[pousr]_[A-Za-z0-9]{20,}|xox[baprs]-[A-Za-z0-9-]{10,}|AKIA[0-9A-Z]{12,}|AIza[0-9A-Za-z_-]{20,}|eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}|(?=[A-Za-z0-9+/]*[0-9])[A-Za-z0-9+/]{40,}={0,2})\b/;
const SECRET_WORD_RE = /\b(password|passwd|secret|api[_-]?key|access[_-]?token|private[_-]?key|bearer)\b/i;

/**
 * Derive a publishable intent string from a user prompt.
 * Returns null for trivial prompts (too short, ack words, slash commands) and
 * for anything that looks like it carries a secret (better to share nothing).
 */
export function deriveIntent(prompt: string): string | null {
  if (!prompt || prompt.length < 25) return null;
  if (prompt.startsWith("/")) return null;
  if (TRIVIAL_RE.test(prompt.trim())) return null;
  // Never publish prompts that look like they carry credentials.
  if (SECRET_RE.test(prompt) || SECRET_WORD_RE.test(prompt)) return null;

  // First line, collapse whitespace
  const firstLine = prompt.split("\n")[0]!.replace(/\s+/g, " ").trim();
  if (firstLine.length === 0) return null;

  if (firstLine.length <= 100) return firstLine;
  return firstLine.slice(0, 97) + "...";
}

// ── shareIntentViaMcp ───────────────────────────────────────────────────────

/**
 * Call POST /api/mcp with a aethereum__share_intent tools/call.
 * If a bare tools/call fails (no session), sends initialize first then retries.
 * Swallows all errors; 4s timeout.
 */
export async function shareIntentViaMcp(
  url: string,
  token: string,
  text: string,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  const endpoint = `${url}/api/mcp`;
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json, text/event-stream",
    Authorization: `Bearer ${token}`,
  };
  const toolCall = JSON.stringify({
    jsonrpc: "2.0",
    id: 1,
    method: "tools/call",
    params: { name: "aethereum__share_intent", arguments: { text } },
  });

  try {
    const sig = AbortSignal.timeout(1_500);
    const res = await fetchImpl(endpoint, { method: "POST", headers, body: toolCall, signal: sig });

    // If the server returned an error that looks like "not initialized", do the
    // initialize round then retry. Any 4xx other than session-init errors we also
    // try the init round (stateless servers generally need initialize first).
    if (!res.ok || res.status === 400) {
      await _initializeThenCall(endpoint, headers, text, fetchImpl);
      return;
    }

    // Drain the body (required to detect JSON-RPC errors)
    const body = await res.text().catch(() => "");
    // If response is a JSON-RPC error, retry with initialize
    if (body.includes('"error"') && body.includes('"code"')) {
      await _initializeThenCall(endpoint, headers, text, fetchImpl);
    }
  } catch {
    // timeout or network error — swallow
  }
}

async function _initializeThenCall(
  endpoint: string,
  headers: Record<string, string>,
  text: string,
  fetchImpl: typeof fetch,
): Promise<void> {
  try {
    const sig1 = AbortSignal.timeout(1_500);
    const initBody = JSON.stringify({
      jsonrpc: "2.0",
      id: 0,
      method: "initialize",
      params: {
        protocolVersion: "2024-11-05",
        clientInfo: { name: "hive-hook", version: "1.0.0" },
        capabilities: {},
      },
    });
    const initRes = await fetchImpl(endpoint, {
      method: "POST",
      headers,
      body: initBody,
      signal: sig1,
    });

    // Capture mcp-session-id if the server returns one (stateful mode)
    const sessionId = initRes.headers.get("mcp-session-id");
    await initRes.text().catch(() => ""); // drain

    const callHeaders: Record<string, string> = { ...headers };
    if (sessionId) callHeaders["mcp-session-id"] = sessionId;

    const sig2 = AbortSignal.timeout(1_500);
    const toolCall = JSON.stringify({
      jsonrpc: "2.0",
      id: 1,
      method: "tools/call",
      params: { name: "aethereum__share_intent", arguments: { text } },
    });
    const callRes = await fetchImpl(endpoint, {
      method: "POST",
      headers: callHeaders,
      body: toolCall,
      signal: sig2,
    });
    await callRes.text().catch(() => ""); // drain
  } catch {
    // swallow all errors
  }
}

// ── fetchContext ─────────────────────────────────────────────────────────────

/**
 * GET /api/sync/context (consuming alerts unless peek=true).
 * Returns null on any failure.
 */
export async function fetchContext(
  url: string,
  token: string,
  opts: { peek?: boolean } = {},
  fetchImpl: typeof fetch = fetch,
): Promise<ContextResponse | null> {
  try {
    const qs = opts.peek ? "?peek=1" : "";
    // Measured production latency on this endpoint is ~1.3-1.9s (sequential auth +
    // rate-limit + context-read round trips) — a 1500ms abort silently dropped
    // roughly half of all real calls. 3000ms leaves real margin; callers race this
    // against their own outer hook deadline, so a slow network still can't stall
    // the agent past the hook's overall budget.
    const sig = AbortSignal.timeout(3_000);
    const res = await fetchImpl(`${url}/api/sync/context${qs}`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: sig,
    });
    if (!res.ok) return null;
    return (await res.json()) as ContextResponse;
  } catch {
    return null;
  }
}

// ── heartbeat ────────────────────────────────────────────────────────────────

/**
 * POST /api/sync/presence — silent heartbeat. Never throws.
 */
export async function heartbeat(
  url: string,
  token: string,
  building?: string,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  try {
    // See fetchContext's comment: 1500ms raced real production latency too closely.
    const sig = AbortSignal.timeout(2_500);
    await fetchImpl(`${url}/api/sync/presence`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ building }),
      signal: sig,
    });
  } catch {
    // silent
  }
}
